import { useState, useEffect } from 'react'
import { Trophy, Medal, Award, Users, TrendingUp, Crown, Calendar, Target } from 'lucide-react'
import { LeaderboardEntry, TripLeaderboardEntry } from '@/types'
import { getAllTimeLeaderboard, getTripParticipants } from '@/lib/firestore'

interface LeaderboardProps {
  type: 'all-time' | 'trip'
  tripId?: string
  currentUserId?: string
  className?: string
  onUserClick?: (userId: string) => void
}

export function Leaderboard({ type, tripId, currentUserId, className = '', onUserClick }: LeaderboardProps) {
  const [entries, setEntries] = useState<LeaderboardEntry[] | TripLeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadLeaderboard = async () => {
      try {
        setLoading(true)
        setError(null)

        if (type === 'all-time') {
          const data = await getAllTimeLeaderboard(20)
          setEntries(data)
        } else if (type === 'trip' && tripId) {
          const data = await getTripParticipants(tripId)
          setEntries(data)
        }
      } catch (err) {
        console.error('Error loading leaderboard:', err)
        setError('Failed to load leaderboard')
      } finally {
        setLoading(false)
      }
    }

    loadLeaderboard()
  }, [type, tripId])

  const getRankIcon = (rank: number, isCurrentUser: boolean) => {
    // Larger icons for better mobile visibility
    const iconSize = "h-6 w-6 sm:h-5 sm:w-5";
    
    if (rank === 1) {
      return <Crown className={`${iconSize} ${isCurrentUser ? 'text-yellow-600' : 'text-yellow-500'}`} />
    } else if (rank === 2) {
      return <Medal className={`${iconSize} ${isCurrentUser ? 'text-gray-600' : 'text-gray-500'}`} />
    } else if (rank === 3) {
      return <Award className={`${iconSize} ${isCurrentUser ? 'text-amber-700' : 'text-amber-600'}`} />
    }
    return <span className={`text-base sm:text-sm font-bold ${isCurrentUser ? 'text-blue-600' : 'text-gray-600'}`}>#{rank}</span>
  }

  const formatDate = (date?: Date, compact?: boolean) => {
    if (!date) return 'Never'
    if (compact) {
      // More concise format for mobile
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric'
      }).format(date)
    }
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-3 mb-3">
              <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded w-2/3 mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-1/3"></div>
              </div>
              <div className="h-4 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border p-6 ${className}`}>
        <div className="text-center text-red-600">
          <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border ${className}`}>
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center space-x-2">
            <Trophy className="h-6 w-6 sm:h-5 sm:w-5 text-[#42a5f5] flex-shrink-0" />
            <h3 className="text-lg sm:text-lg font-semibold text-gray-900">
              {type === 'all-time' ? 'All-Time Leaderboard' : 'Trip Leaderboard'}
            </h3>
          </div>
          <div className="flex items-center space-x-1 text-sm text-gray-500">
            <Users className="h-4 w-4 flex-shrink-0" />
            <span>{entries.length} participants</span>
          </div>
        </div>
      </div>

      {/* Leaderboard Entries */}
      <div className="p-4 sm:p-6">
        {entries.length === 0 ? (
          <div className="text-center py-8">
            <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500 mb-2">No participants yet</p>
            <p className="text-sm text-gray-400">
              {type === 'all-time' 
                ? 'Start spotting teams to appear on the leaderboard!' 
                : 'Share this trip with friends to start competing!'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4 sm:space-y-3">
            {entries.map((entry, index) => {
              const isCurrentUser = entry.userId === currentUserId
              const isTripEntry = 'tripId' in entry
              
              return (
                <div
                  key={`${entry.userId}-${index}`}
                  onClick={() => onUserClick?.(entry.userId)}
                  className={`flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 p-4 sm:p-4 rounded-lg transition-colors min-h-[80px] ${
                    onUserClick ? 'cursor-pointer active:bg-gray-200 touch-manipulation' : ''
                  } ${
                    isCurrentUser 
                      ? 'bg-blue-50 border-2 border-blue-200' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  {/* Mobile Layout: Top Row */}
                  <div className="flex items-center space-x-4 sm:contents">
                    {/* Rank */}
                    <div className="flex-shrink-0 w-10 h-10 sm:w-8 sm:h-8 flex items-center justify-center">
                      {getRankIcon(entry.rank, isCurrentUser)}
                    </div>

                    {/* Profile Image */}
                    <div className="flex-shrink-0">
                      {entry.profileImageUrl ? (
                        <img
                          src={entry.profileImageUrl}
                          alt={`${entry.userName}'s profile`}
                          className="w-12 h-12 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-gray-200"
                          onError={(e) => {
                            // Fallback to initials if image fails to load
                            const target = e.target as HTMLImageElement
                            target.style.display = 'none'
                            const fallback = target.nextElementSibling as HTMLElement
                            if (fallback) fallback.style.display = 'flex'
                          }}
                        />
                      ) : null}
                      <div 
                        className={`w-12 h-12 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-sm border-2 border-gray-200 ${
                          entry.profileImageUrl ? 'hidden' : 'flex'
                        } ${isCurrentUser ? 'bg-blue-500' : 'bg-gray-400'}`}
                        style={{ display: entry.profileImageUrl ? 'none' : 'flex' }}
                      >
                        {entry.userName.charAt(0).toUpperCase()}
                      </div>
                    </div>

                    {/* User Info - Mobile: Full width */}
                    <div className="flex-1 min-w-0 sm:min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2">
                        <p className={`text-base sm:text-sm font-medium leading-tight ${
                          isCurrentUser ? 'text-blue-900' : 'text-gray-900'
                        }`}
                        style={{
                          // Full username on mobile, truncate on desktop only if very long
                          overflow: 'visible',
                          textOverflow: 'unset',
                          whiteSpace: 'normal'
                        }}>
                          {entry.userName}
                          {isCurrentUser && ' (You)'}
                        </p>
                        {isTripEntry && (entry as TripLeaderboardEntry).isCreator && (
                          <span className="inline-block mt-1 sm:mt-0 sm:ml-2 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                            Creator
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Score - Always visible on mobile right side */}
                    <div className="flex-shrink-0 text-right sm:hidden">
                      <div className={`text-2xl font-bold ${
                        isCurrentUser ? 'text-blue-600' : 'text-gray-900'
                      }`}>
                        {entry.totalSpots}
                      </div>
                    </div>
                  </div>

                  {/* Mobile Layout: Bottom Row - Stats */}
                  <div className="sm:hidden">
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div className="flex items-center space-x-1 text-sm text-gray-600">
                        <Target className="h-4 w-4 flex-shrink-0" />
                        <span>{entry.totalSpots} spots</span>
                      </div>
                      <div className="flex items-center space-x-1 text-sm text-gray-600">
                        <Award className="h-4 w-4 flex-shrink-0" />
                        <span>{entry.uniqueLeagues} leagues</span>
                      </div>
                      {entry.lastSpottedAt && (
                        <div className="col-span-2 flex items-center space-x-1 text-sm text-gray-500">
                          <Calendar className="h-4 w-4 flex-shrink-0" />
                          <span>Last active: {formatDate(entry.lastSpottedAt, true)}</span>
                        </div>
                      )}
                    </div>
                    {entry.progress > 0 && (
                      <div className="flex items-center space-x-2 mt-3">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-300 ${
                              isCurrentUser ? 'bg-blue-500' : 'bg-gray-400'
                            }`}
                            style={{ width: `${Math.min(entry.progress, 100)}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 font-medium">
                          {entry.progress}%
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Desktop Layout: Stats inline */}
                  <div className="hidden sm:block flex-1 min-w-0">
                    <div className="flex items-center space-x-4 mt-1">
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Target className="h-3 w-3" />
                        <span>{entry.totalSpots} spots</span>
                      </div>
                      <div className="flex items-center space-x-1 text-xs text-gray-500">
                        <Award className="h-3 w-3" />
                        <span>{entry.uniqueLeagues} leagues</span>
                      </div>
                      {entry.lastSpottedAt && (
                        <div className="flex items-center space-x-1 text-xs text-gray-500">
                          <Calendar className="h-3 w-3" />
                          <span>Last: {formatDate(entry.lastSpottedAt)}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Desktop Progress */}
                  <div className="hidden sm:block flex-shrink-0 text-right">
                    <div className={`text-lg font-bold ${
                      isCurrentUser ? 'text-blue-600' : 'text-gray-900'
                    }`}>
                      {entry.totalSpots}
                    </div>
                    {entry.progress > 0 && (
                      <div className="flex items-center space-x-2 mt-1">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all duration-300 ${
                              isCurrentUser ? 'bg-blue-500' : 'bg-gray-400'
                            }`}
                            style={{ width: `${Math.min(entry.progress, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">
                          {entry.progress}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {entries.length > 0 && (
        <div className="px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-gray-500">
            <span className="text-center sm:text-left">
              {type === 'all-time' 
                ? 'Ranked by total spots and unique leagues' 
                : `Competition started ${type === 'trip' && entries.length > 0 ? 
                    formatDate((entries[0] as TripLeaderboardEntry).joinedAt) : 'recently'
                  }`
              }
            </span>
            <div className="flex items-center justify-center sm:justify-start space-x-1">
              <TrendingUp className="h-3 w-3" />
              <span>Live rankings</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}